import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { uniq } from 'lodash';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Checkbox, PrimaryNavigationMenu, PrimaryNavigationMenuItem, TertiaryNavigationActionButton } from 'components/ui';
import { toString } from 'utilities/toString';
import { FundFilterMenuProps } from './FundFilterMenu.props';

interface FundFilterOption {
  id: string;
  label: string;
}

const FUND_FILTER_TEST_IDS = {
  trigger: 'cash-flow-ledger-filter-by-fund-btn',
  option: (id: string) => `cash-flow-ledger-filter-by-fund-option-${id}`,
};

const FundFilterMenu = ({ cashFlows, setActiveFundIds, reset }: FundFilterMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);
  const [{ fundList }] = useStore() as unknown as UseStoreValues;

  // Only funds actually referenced by a ledger row on this page are offered.
  const fundOptions = useMemo<FundFilterOption[]>(() => {
    if (!cashFlows?.length || !fundList?.length) {
      return [];
    }

    const fundIds = uniq(cashFlows.map(row => row.owner_fund).filter((id): id is number => id != null));

    return fundIds
      .map(id => fundList.find(fund => fund.id === id))
      .filter((fund): fund is NonNullable<typeof fund> => Boolean(fund))
      .map(fund => ({ id: toString(fund.id), label: fund.name as string }));
  }, [cashFlows, fundList]);

  useEffect(() => {
    if (reset) {
      setSelectedFundIds([]);
    }
  }, [reset]);

  // No valid selection yet (initial load/reset) defaults to every fund, never to zero.
  const effectiveFundIds = useMemo(() => {
    const valid = selectedFundIds.filter(id => fundOptions.some(option => option.id === id));
    if (valid.length) {
      return valid;
    }
    return fundOptions.map(option => option.id);
  }, [fundOptions, selectedFundIds]);

  useEffect(() => {
    setActiveFundIds(effectiveFundIds);
  }, [effectiveFundIds, setActiveFundIds]);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleToggle = useCallback(
    (option: FundFilterOption) => {
      const isChecked = effectiveFundIds.includes(option.id);
      if (isChecked) {
        // At least one fund must stay selected at all times.
        if (effectiveFundIds.length <= 1) {
          return;
        }
        setSelectedFundIds(effectiveFundIds.filter(id => id !== option.id));
        return;
      }
      setSelectedFundIds([...effectiveFundIds, option.id]);
    },
    [effectiveFundIds]
  );

  if (fundOptions.length <= 1) {
    return null;
  }

  return (
    <>
      <TertiaryNavigationActionButton
        data-testid={FUND_FILTER_TEST_IDS.trigger}
        endIcon={<ArrowDropDownIcon fontSize="inherit" />}
        onClick={handleOpen}
        variant="secondary">
        Filter by Fund
      </TertiaryNavigationActionButton>
      <PrimaryNavigationMenu anchorEl={anchorEl} onClose={handleClose} open={Boolean(anchorEl)}>
        {fundOptions.map(option => {
          const isActive = effectiveFundIds.includes(option.id);
          const isOnlySelection = isActive && effectiveFundIds.length === 1;
          return (
            <PrimaryNavigationMenuItem
              aria-selected={isActive}
              data-testid={FUND_FILTER_TEST_IDS.option(option.id)}
              isActive={isActive}
              key={option.id}
              leadingSlot={<Checkbox checked={isActive} disabled={isOnlySelection} readOnly visualVariant="selection" />}
              onClick={() => handleToggle(option)}
              role="option">
              {option.label}
            </PrimaryNavigationMenuItem>
          );
        })}
      </PrimaryNavigationMenu>
    </>
  );
};

export default FundFilterMenu;
